import { MerchantDropdown } from "@components/dropdowns/MerchantDropdown";
import { invoiceServices } from "@services/apis/Invoice";
import { GetOfframpBanksData } from "@services/models/InvoiceApprovals/GetOfframpBanksModel";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface BankAccountSelectionComponentProps {
  onBankSelection: (
    bankData: {
      bankId: number;
      bankName: string;
      accountName: string;
      accountNo: string;
    } | null
  ) => void;
  selectedBankId?: number;
  label?: string;
}

const BankAccountSelectionComponent: React.FC<
  BankAccountSelectionComponentProps
> = ({ onBankSelection, selectedBankId, label = "Bank Account Selection" }) => {
  const [bankData, setBankData] = useState<GetOfframpBanksData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedBankName, setSelectedBankName] = useState<string>("");
  const [selectedAccountName, setSelectedAccountName] = useState<string>("");
  const [selectedAccountNo, setSelectedAccountNo] = useState<string>("");

  const { handleError } = useSnackbarMessage();

  // Get unique bank names
  const uniqueBankNames = Array.from(
    new Set(bankData.map((bank) => bank.bankName))
  ).map((bankName, index) => ({
    id: index + 1,
    name: bankName,
    bankName: bankName,
  }));

  // Get unique account names for selected bank
  const uniqueAccountNames = Array.from(
    new Set(
      bankData
        .filter((bank) => bank.bankName === selectedBankName)
        .map((bank) => bank.accountName)
    )
  ).map((accountName, index) => ({
    id: index + 1,
    name: accountName,
    accountName: accountName,
  }));

  // Get account numbers for selected bank and account name
  const accountNumbers = bankData
    .filter(
      (bank) =>
        bank.bankName === selectedBankName &&
        bank.accountName === selectedAccountName
    )
    .map((bank, index) => ({
      id: index + 1,
      name: bank.accountNo,
      accountNo: bank.accountNo,
      bankId: bank.bankId,
    }));

  const fetchBankData = async () => {
    try {
      setLoading(true);
      const response = await invoiceServices.getOfframpBanks();
      // Filter only active banks
      const activeBanks = response.data.filter((bank) => bank.isActive === 1);
      setBankData(activeBanks);
    } catch (error) {
      handleError({ message: "Failed to load bank data" });
    } finally {
      setLoading(false);
    }
  };

  const handleBankNameSelection = (value: string | number) => {
    const selectedItem = uniqueBankNames.find(
      (item) => item.id === Number(value)
    );
    if (selectedItem) {
      setSelectedBankName(selectedItem.bankName);
      setSelectedAccountName("");
      setSelectedAccountNo("");
      onBankSelection(null);
    }
  };

  const handleAccountNameSelection = (value: string | number) => {
    const selectedItem = uniqueAccountNames.find(
      (item) => item.id === Number(value)
    );
    if (selectedItem) {
      setSelectedAccountName(selectedItem.accountName);
      setSelectedAccountNo("");
      onBankSelection(null);
    }
  };

  const handleAccountNumberSelection = (value: string | number) => {
    const selectedItem = accountNumbers.find(
      (item) => item.id === Number(value)
    );
    if (selectedItem) {
      setSelectedAccountNo(selectedItem.accountNo);

      // Find the complete bank data
      const selectedBank = bankData.find(
        (bank) =>
          bank.bankName === selectedBankName &&
          bank.accountName === selectedAccountName &&
          bank.accountNo === selectedItem.accountNo
      );

      if (selectedBank) {
        onBankSelection({
          bankId: selectedBank.bankId,
          bankName: selectedBank.bankName,
          accountName: selectedBank.accountName,
          accountNo: selectedBank.accountNo,
        });
      }
    }
  };

  // Initialize component with selected bank data if provided
  useEffect(() => {
    if (selectedBankId && bankData.length > 0) {
      const selectedBank = bankData.find(
        (bank) => bank.bankId === selectedBankId
      );
      if (selectedBank) {
        setSelectedBankName(selectedBank.bankName);
        setSelectedAccountName(selectedBank.accountName);
        setSelectedAccountNo(selectedBank.accountNo);
        onBankSelection({
          bankId: selectedBank.bankId,
          bankName: selectedBank.bankName,
          accountName: selectedBank.accountName,
          accountNo: selectedBank.accountNo,
        });
      }
    }
  }, [selectedBankId, bankData]);

  useEffect(() => {
    fetchBankData();
  }, []);

  if (loading) {
    return <Skeleton height={100} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="text-[10px] font-semibold text-text-base">{label}</div>

      {/* Bank Name Dropdown */}
      {uniqueBankNames.length > 0 && (
        <MerchantDropdown
          listOfValues={uniqueBankNames}
          value={
            uniqueBankNames.find((item) => item.bankName === selectedBankName)
              ?.id || undefined
          }
          onChange={handleBankNameSelection}
          label="Select Bank Name"
          color="bg-main-container-header"
          baseColor="bg-bg-page-body"
          borderColor="border-subtle"
          expandedBorderColor="border-button-primary"
        />
      )}

      {/* Account Name Dropdown */}
      {selectedBankName && uniqueAccountNames.length > 0 && (
        <MerchantDropdown
          listOfValues={uniqueAccountNames}
          value={
            uniqueAccountNames.find(
              (item) => item.accountName === selectedAccountName
            )?.id || undefined
          }
          onChange={handleAccountNameSelection}
          label="Select Account Name"
          color="bg-main-container-header"
          baseColor="bg-bg-page-body"
          borderColor="border-subtle"
          expandedBorderColor="border-button-primary"
        />
      )}

      {/* Account Number Dropdown */}
      {selectedAccountName && accountNumbers.length > 0 && (
        <MerchantDropdown
          listOfValues={accountNumbers}
          value={
            accountNumbers.find((item) => item.accountNo === selectedAccountNo)
              ?.id || undefined
          }
          onChange={handleAccountNumberSelection}
          label="Select Account Number"
          color="bg-main-container-header"
          baseColor="bg-bg-page-body"
          borderColor="border-subtle"
          expandedBorderColor="border-button-primary"
        />
      )}
    </div>
  );
};

export default BankAccountSelectionComponent;
